import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store/store';
import ProductForm from '../../../admin/ProductForm/ProductForm';
import Placeholder from '../../../UI/Placeholder/Placeholder';
import Actions from '../../../layouts/adminLayouts/Actions/Actions';
import Content from '../../../layouts/adminLayouts/Content/Content';
import ProductsList from '../../../admin/ProductsList/ProductsList';
import { openProductForm, closeProductFormSafely } from '../../../../store/uiSlice';
import { Category, Product } from '../../../../types/common';
import Loader from '../../../UI/Loader/Loader';
import Button from '../../../UI/Button/Button';
import {
    ADD_FILLERTYPE_AND_CATEGORY_MESSAGE,
    ADD_FILLERTYPE_MESSAGE,
    ADD_FABRICTYPE_AND_CATEGORY_MESSAGE,
    ADD_FABRICTYPE_MESSAGE,
    ADD_CATEGORY_MESSAGE,
    NO_PRODUCTS_MESSAGE,
} from '../../../../constants/messages';
import { exportProductsToExcel } from '../../../../utils/exportToExcel';
import { str } from '../../../../utils/helpers';

// порядок категорий по умолчанию
const DEFAULT_CATEGORY_ORDER = ['pillows', 'blankets', 'accessories', 'bed-linen'];
const CATEGORY_RANK: Record<string, number> = DEFAULT_CATEGORY_ORDER.reduce<Record<string, number>>((acc, key, idx) => {
    acc[key] = idx;
    return acc;
}, {});
const normalize = (v: string | undefined | null) => (v ? v.toString().trim().toLowerCase().replace(/ё/g, 'е') : '');
const getCategoryKey = (category: Category) => {
    const anyCat = category as unknown as { url?: string; slug?: string; code?: string; name?: string };
    return normalize(anyCat.url ?? anyCat.slug ?? anyCat.code ?? anyCat.name ?? '');
};
const ruCompare = (a: string, b: string) => a.localeCompare(b, 'ru', { sensitivity: 'base' });
const defaultComparator = (a: Product, b: Product) => {
    const aKey = getCategoryKey(a.category);
    const bKey = getCategoryKey(b.category);
    const aRank = CATEGORY_RANK[aKey] ?? 4;
    const bRank = CATEGORY_RANK[bKey] ?? 4;
    if (aRank !== bRank) return aRank - bRank;
    if (aRank === 4) {
        const catDiff = ruCompare(str(a.category.name), str(b.category.name));
        if (catDiff !== 0) return catDiff;
        return ruCompare(str(a.name), str(b.name));
    }
    return ruCompare(str(a.name), str(b.name));
};
const defaultSort = (list: Product[]) => [...list].sort(defaultComparator);

const ProductsPage: React.FC = () => {
    const {
        fillerType,
        fabricType,
        category,
        product,
        prodGroup,
        ui: { isProductFormOpen },
    } = useSelector((state: RootState) => state);
    const dispatch = useDispatch<AppDispatch>();
    const isDataLoading = [fillerType.isLoading, fabricType.isLoading, category.isLoading, product.isLoading, prodGroup.isLoading].some(Boolean);
    const hasCategories = category.categories.length > 0;
    const hasProducts = product.products.length > 0;
    const hasTypesOfFiller = fillerType.typesOfFiller.length > 0;
    const hasTypesOfFabric = fabricType.typesOfFabric.length > 0;
    // const hasGroups = prodGroup.groups.length > 0;
    const isFetchingError = product.error.isError;
    const isProductListVisible = hasCategories && hasProducts && hasTypesOfFiller && hasTypesOfFabric && !isProductFormOpen && !isFetchingError;

    const openForm = () => dispatch(openProductForm());
    const closeForm = () => dispatch(closeProductFormSafely());

    const handleExport = async () => {
        try {
            await exportProductsToExcel(defaultSort(product.products));
        } catch (e) {
            console.error('Ошибка экспорта', e);
        }
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <Actions
                    title={'Товары'}
                    onAction={openForm}
                    actionBtnText={'Добавить товар'}
                    isOpen={isProductFormOpen}
                    isDisabled={!hasCategories || !hasTypesOfFiller || !hasTypesOfFabric}
                />
                <Button mode={'secondary'} onClick={handleExport} isDisabled={!hasProducts || isDataLoading || isProductFormOpen}>
                    Выгрузить в Excel
                </Button>
            </div>
            <Content>
                <>
                    {isDataLoading && <Loader />}
                    {!isDataLoading && isFetchingError && <Placeholder text={product.error.message} />}
                    {!isDataLoading && !isFetchingError && !hasCategories && <Placeholder text={ADD_CATEGORY_MESSAGE} />}
                    {!isDataLoading && !isFetchingError && !hasTypesOfFiller && <Placeholder text={ADD_FILLERTYPE_MESSAGE} />}
                    {!isDataLoading && !isFetchingError && !hasTypesOfFabric && <Placeholder text={ADD_FABRICTYPE_MESSAGE} />}
                    {!isDataLoading && !isFetchingError && !hasTypesOfFiller && !hasCategories && <Placeholder text={ADD_FILLERTYPE_AND_CATEGORY_MESSAGE} />}
                    {!isDataLoading && !isFetchingError && !hasTypesOfFabric && !hasCategories && <Placeholder text={ADD_FABRICTYPE_AND_CATEGORY_MESSAGE} />}
                    {!isDataLoading && !isFetchingError && !hasProducts && !hasTypesOfFiller && !hasTypesOfFabric && !hasCategories && (
                        <Placeholder text={NO_PRODUCTS_MESSAGE} />
                    )}
                    {isProductListVisible && (
                        <ProductsList
                            products={product.products}
                            onOpen={openForm}
                            isLoading={product.isLoading}
                            categories={category.categories}
                            typesOfFiller={fillerType.typesOfFiller}
                            typesOfFabric={fabricType.typesOfFabric}
                            defaultSort={defaultSort}
                        />
                    )}
                    {isProductFormOpen && (
                        <ProductForm
                            onClose={closeForm}
                            categories={category.categories}
                            typesOfFiller={fillerType.typesOfFiller}
                            typesOfFabric={fabricType.typesOfFabric}
                        />
                    )}
                </>
            </Content>
        </>
    );
};

export default ProductsPage;
